import { isFeatureFlagEnabled, PORTAL_SECTION_FLAGS } from "@/services/analytics/featureFlags";
import { useAuthStore } from "@/stores/auth";

type PortalSection = keyof typeof PORTAL_SECTION_FLAGS;

/**
 * Whether the current user may open each portal section — the `canAccess` predicate of that
 * section's route in `router/index.ts`, AND the test the landing page below is chosen by.
 *
 * Defined once, here, so the two cannot disagree. If they did, the landing page could be a section
 * the router then denies: the guard's fallback would point at a route the user can never reach,
 * which is a dead end rather than a fallback (Settings for a funder was exactly that shape while
 * the funder rule lived only on the route).
 *
 * Every section is behind its own Mixpanel gate (fail-closed). Settings is also closed to the
 * funder role (spec 2026-09-24) — UI-only in v1; the settings write endpoints close to funders
 * server-side in round 2.
 */
export const PORTAL_SECTION_ACCESS: Record<PortalSection, () => boolean> = {
  acquisition: () => isFeatureFlagEnabled(PORTAL_SECTION_FLAGS.acquisition),
  // Counts only, so funders see it too (spec 2026-09-24; the route returns no answers).
  campaigns: () => isFeatureFlagEnabled(PORTAL_SECTION_FLAGS.campaigns),
  capital: () => isFeatureFlagEnabled(PORTAL_SECTION_FLAGS.capital),
  cases: () => isFeatureFlagEnabled(PORTAL_SECTION_FLAGS.cases),
  intake: () => isFeatureFlagEnabled(PORTAL_SECTION_FLAGS.intake),
  leads: () => isFeatureFlagEnabled(PORTAL_SECTION_FLAGS.leads),
  // Counts only — no lead data — so funders see it too (RAP-741).
  relayImpact: () => isFeatureFlagEnabled(PORTAL_SECTION_FLAGS.relayImpact),
  settings: () =>
    isFeatureFlagEnabled(PORTAL_SECTION_FLAGS.settings) && !useAuthStore().isPortalFunder,
  summary: () => isFeatureFlagEnabled(PORTAL_SECTION_FLAGS.summary),
};

/**
 * Where a referring-portal user lands, in order of preference, when the section they asked for is
 * closed to them (or they asked for `/portal` itself).
 *
 * Plain nav order, so Summary — the first item in the sidebar — is the landing page (RAP-680).
 * Leads used to head this list, on the reasoning that Summary's tiles are empty until a firm files
 * its first spend report; the product call is to land on Summary regardless. Any section can be
 * closed (its Mixpanel gate, or a role rule), so the landing page is computed, never assumed: a
 * user without Summary lands on the next section they can open.
 *
 * Candidates are checked against the SECTION predicate only (`PORTAL_SECTION_ACCESS`), not the full
 * `canAccessRoute` chain — this module cannot import the router without a cycle. That is enough
 * today because the guard only asks for a portal landing once the layout's own gate and the
 * deny-by-default rules have passed; a future deny rule on the portal subtree must be mirrored
 * here, or the landing page could again be a route the router refuses.
 */
export const LANDING_ORDER = [
  { name: "PortalSummary", section: "summary" },
  { name: "PortalLeads", section: "leads" },
  { name: "PortalAcquisition", section: "acquisition" },
  { name: "PortalCampaigns", section: "campaigns" },
  { name: "PortalIntake", section: "intake" },
  { name: "PortalRelayImpact", section: "relayImpact" },
  { name: "PortalCases", section: "cases" },
  { name: "PortalCapital", section: "capital" },
  { name: "PortalSettings", section: "settings" },
] as const satisfies readonly { name: string; section: PortalSection }[];

/**
 * Every section must be ranked as a landing candidate, not only gated. `PORTAL_SECTION_ACCESS` is
 * a `Record`, so a new flag already forces a predicate there; without this, the same section could
 * be left out of `LANDING_ORDER` and a firm with only that section switched on would land on the
 * empty `/portal` page with a green build. Fails to compile, naming the section, until it is ranked.
 * (That each `name` is a real route is asserted in `canAccess-gates.test.ts`.)
 */
type UnrankedSections = Exclude<PortalSection, (typeof LANDING_ORDER)[number]["section"]>;
const EVERY_SECTION_RANKED: [UnrankedSections] extends [never] ? true : UnrankedSections = true;
void EVERY_SECTION_RANKED;

/** Route name of `/portal` itself — the "nothing is switched on yet" page. Never gated by a flag. */
export const PORTAL_HOME = "PortalHome";

/** The first portal section the current user can open, or `null` when none is open to them. */
export function firstEnabledPortalSection(): null | string {
  return LANDING_ORDER.find(({ section }) => PORTAL_SECTION_ACCESS[section]())?.name ?? null;
}
